import type { Identifier, XYCoord } from 'dnd-core';
import { useEffect, useRef, useState } from 'react';
import { IconButton, TextField } from '@tryhackme/thm-ui-components';
import { faGripDotsVertical, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { useDrag, useDrop } from 'react-dnd';
import { useFormContext } from 'src/common/form';
import { Task } from 'src/common/interfaces/manage-room';
import { SUMMERNOTE_QUESTION_OPTIONS, Summernote } from 'src/common/components/summernote';
import { DeleteModal } from 'src/common/delete-modal';
import { AI_TUTOR_NAME } from 'src/common/constants';
import { ScoreTypeEnum } from '../../../general-settings/components/room-settings-section/room-settings-section.constants';
import { InlineRichTextEditor } from '../task-form/editor/inline-editor';
import { useLocalStorage } from '../task-form/hooks/use-local-storage';
import { PointsWrapper, QuestionHeaderContainer, QuestionNumberWrapper, StyledButtonsContainer, StyledDeleteQuestionContainer, StyledEditorContainer, StyledGripIcon, StyledGripIconContainer, StyledInputContainer, StyledPlusIcon, StyledQuestionContainer, StyledQuestionContentContainer, StyledQuestionErrorText, StyledQuestionLabel, StyledQuestionNumber, StyledQuestionOptionButton, StyledQuestionOptionsTextInputWrapper, StyledTextField, StyledTrashIcon } from './question-card.styles';
import { QuestionCardProps } from './question-card.types';
export const QuestionCard = ({
  roomDetails,
  index,
  showRemoveIcon,
  handleRemoveQuestion,
  fieldId,
  handleMove,
  findQuestion,
  isContentDev
}: QuestionCardProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const {
    setValue,
    register,
    formState: {
      errors
    },
    getValues
  } = useFormContext<Task>();
  const answer = getValues(`infoList.${index}.answer`);
  const hint = getValues(`infoList.${index}.hint`);
  const prompts = getValues(`infoList.${index}.prompts`);
  const [addAnswer, setAddAnswer] = useState(false);
  const [addHint, setAddHint] = useState(false);
  const [addPrompts, setAddPrompts] = useState(false);
  const [overInput, setOverInput] = useState(false);
  const [hintModal, setHintModal] = useState(false);
  const [answerModal, setAnswerModal] = useState(false);
  const [promptsModal, setPromptsModal] = useState(false);
  useEffect(() => {
    if (answer) setAddAnswer(Boolean(answer));
    if (hint) setAddHint(Boolean(hint));
    if (prompts) setAddPrompts(Boolean(prompts));
  }, [answer, hint, prompts]);
  const [{
    handlerId
  }, drop] = useDrop<{
    id: string;
    index: number;
  }, void, {
    handlerId: Identifier | null;
  }>({
    accept: 'question',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = findQuestion(item.id);
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      handleMove(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    }
  });
  const [{
    isDragging
  }, drag] = useDrag({
    type: 'question',
    item: () => ({
      id: fieldId,
      index
    }),
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });
  drag(drop(ref));
  const handleRemoveQuestionInputs = (input: string) => {
    if (input === 'answer') {
      setValue(`infoList.${index}.answer`, '', {
        shouldDirty: true
      });
      setAddAnswer(false);
    }
    if (input === 'hint') {
      setValue(`infoList.${index}.hint`, '', {
        shouldDirty: true
      });
      setAddHint(false);
    }
    if (input === 'prompts') {
      setValue(`infoList.${index}.prompts`, '', {
        shouldDirty: true
      });
      setAddPrompts(false);
    }
  };
  const {
    value: enableNewEditor
  } = useLocalStorage<boolean>('useTinyEditor', false);
  return <>
      <StyledQuestionContainer ref={ref} data-handler-id={handlerId} isError={Boolean(errors?.infoList?.[index])} data-testid="question-card" isDragging={isDragging}
    // hack for Firefox as firefox not let us select inputs that are in draggable=true
    draggable={!overInput} data-sentry-element="StyledQuestionContainer" data-sentry-source-file="question-card.tsx">
        <StyledQuestionContentContainer showRemoveIcon={showRemoveIcon} data-sentry-element="StyledQuestionContentContainer" data-sentry-source-file="question-card.tsx">
          <QuestionHeaderContainer data-sentry-element="QuestionHeaderContainer" data-sentry-source-file="question-card.tsx">
            <QuestionNumberWrapper data-sentry-element="QuestionNumberWrapper" data-sentry-source-file="question-card.tsx">
              <StyledQuestionNumber data-sentry-element="StyledQuestionNumber" data-sentry-source-file="question-card.tsx">{`Question ${index + 1}`}</StyledQuestionNumber>
            </QuestionNumberWrapper>
            {roomDetails.scoreType === ScoreTypeEnum.CUSTOM_POINTS && <PointsWrapper>
                Points{' '}
                <StyledTextField id={`customPoints-${fieldId}`} {...register(`infoList.${index}.customPoints`)} min={0} placeholder="0" type="number" variant="input" />
              </PointsWrapper>}
          </QuestionHeaderContainer>
          <StyledQuestionLabel data-sentry-element="StyledQuestionLabel" data-sentry-source-file="question-card.tsx">Question</StyledQuestionLabel>

          {enableNewEditor ? <InlineRichTextEditor content={getValues(`infoList.${index}.question`)} onChange={value => setValue(`infoList.${index}.question`, value, {
          shouldDirty: true
        })} /> : <StyledEditorContainer onMouseEnter={() => setOverInput(true)} onMouseLeave={() => setOverInput(false)}>
              <Summernote options={SUMMERNOTE_QUESTION_OPTIONS} {...register(`infoList.${index}.question`)} />
            </StyledEditorContainer>}
          {addAnswer && <StyledQuestionOptionsTextInputWrapper>
              <StyledInputContainer>
                <TextField id={`answer-${fieldId}`} label="Answer" {...register(`infoList.${index}.answer`)} variant="input" placeholder="Enter answer here..." onMouseEnter={() => setOverInput(true)} onMouseLeave={() => setOverInput(false)} />
              </StyledInputContainer>

              <IconButton onClick={() => setAnswerModal(true)} data-testid="remove-answer" variant="delete" height="3.6rem" width="3.6rem">
                <StyledTrashIcon icon={faTrashCan} />
              </IconButton>
            </StyledQuestionOptionsTextInputWrapper>}
          {addHint && <StyledQuestionOptionsTextInputWrapper>
              <StyledInputContainer>
                <TextField id={`hint-${fieldId}`} label="Hint" {...register(`infoList.${index}.hint`)} variant="input" placeholder="Enter hint here..." onMouseEnter={() => setOverInput(true)} onMouseLeave={() => setOverInput(false)} />
              </StyledInputContainer>
              <IconButton onClick={() => setHintModal(true)} data-testid="remove-hint" variant="delete" height="3.6rem" width="3.6rem">
                <StyledTrashIcon icon={faTrashCan} />
              </IconButton>
            </StyledQuestionOptionsTextInputWrapper>}
          {isContentDev && addPrompts && <StyledQuestionOptionsTextInputWrapper>
              <StyledInputContainer>
                <TextField id={`prompts-${fieldId}`} label="Prompts" {...register(`infoList.${index}.prompts`)} variant="textarea" placeholder={`Add expected actions to solve the question. Please be as detailed as you can to give ${AI_TUTOR_NAME} enough context`} onMouseEnter={() => setOverInput(true)} onMouseLeave={() => setOverInput(false)} />
              </StyledInputContainer>
              <IconButton onClick={() => setPromptsModal(true)} data-testid="remove-prompts" variant="delete" height="3.6rem" width="3.6rem">
                <StyledTrashIcon icon={faTrashCan} />
              </IconButton>
            </StyledQuestionOptionsTextInputWrapper>}
          {(!addAnswer || !addHint || !addPrompts) && <StyledButtonsContainer>
              {!addAnswer && <StyledQuestionOptionButton variant="outlined" color="secondary" onClick={() => setAddAnswer(true)}>
                  <StyledPlusIcon icon={faPlus} />
                  Add answer
                </StyledQuestionOptionButton>}

              {!addHint && <StyledQuestionOptionButton variant="outlined" color="secondary" onClick={() => setAddHint(true)}>
                  <StyledPlusIcon icon={faPlus} />
                  Add hint
                </StyledQuestionOptionButton>}

              {isContentDev && !addPrompts && <StyledQuestionOptionButton variant="outlined" color="secondary" onClick={() => setAddPrompts(true)}>
                  <StyledPlusIcon icon={faPlus} />
                  Add prompts
                </StyledQuestionOptionButton>}
            </StyledButtonsContainer>}
        </StyledQuestionContentContainer>

        {showRemoveIcon && <StyledDeleteQuestionContainer>
            <IconButton data-testid="remove-question" variant="delete" height="3.6rem" width="3.6rem" onClick={handleRemoveQuestion}>
              <StyledTrashIcon icon={faTrashCan} />
            </IconButton>
          </StyledDeleteQuestionContainer>}

        <StyledGripIconContainer data-sentry-element="StyledGripIconContainer" data-sentry-source-file="question-card.tsx">
          <StyledGripIcon icon={faGripDotsVertical} data-sentry-element="StyledGripIcon" data-sentry-source-file="question-card.tsx" />
        </StyledGripIconContainer>
      </StyledQuestionContainer>
      <StyledQuestionErrorText data-sentry-element="StyledQuestionErrorText" data-sentry-source-file="question-card.tsx">{errors?.infoList?.[index]?.question?.message}</StyledQuestionErrorText>

      <DeleteModal activeIndex={0} open={hintModal} onOpenChange={value => setHintModal(value)} modalTitleText="Delete hint" modalBodyText="Are you sure you want delete this hint?" actionButtonText="Delete hint" handleDeleteAction={() => handleRemoveQuestionInputs('hint')} data-sentry-element="DeleteModal" data-sentry-source-file="question-card.tsx" />

      <DeleteModal activeIndex={0} open={answerModal} onOpenChange={value => setAnswerModal(value)} modalTitleText="Delete answer" modalBodyText="Are you sure you want delete this answer?" actionButtonText="Delete answer" handleDeleteAction={() => handleRemoveQuestionInputs('answer')} data-sentry-element="DeleteModal" data-sentry-source-file="question-card.tsx" />

      <DeleteModal activeIndex={0} open={promptsModal} onOpenChange={value => setPromptsModal(value)} modalTitleText="Delete prompts" modalBodyText="Are you sure you want delete this prompts?" actionButtonText="Delete prompts" handleDeleteAction={() => handleRemoveQuestionInputs('prompts')} data-sentry-element="DeleteModal" data-sentry-source-file="question-card.tsx" />
    </>;
};